/**
 * The freshness line, read as data rather than shown as a sentence.
 *
 * The model writes it as the last line of an answer, in a fixed English form
 * (signals-copilot's freshness_stamp rule + frame.attrs['erp_check']):
 *
 *   Data updated through 2026-09-23 (loaded 2026-09-24 01:00) · matches the ERP (checked 2026-09-24 04:00)
 *
 * As one sentence it read as part of the answer, and "loaded the 24th ...
 * checked the 24th" left the owner asking what happened when (2026-09-24).
 * Split into its facts, each can become a pill with a sentence of its own.
 * Anything it cannot read falls back to the whole line, never to nothing.
 */

export type ErpState = 'match' | 'loading' | 'mismatch';

export interface FreshnessFacts {
  /** YYYY-MM-DD: the last day in the data. */
  through?: string;
  /** "YYYY-MM-DD HH:MM" in ERP local time: when the ERP was read. */
  loadedAt?: string;
  erp?: {
    state: ErpState;
    /** "YYYY-MM-DD HH:MM" or "YYYY-MM-DD": when the audit ran. */
    checkedAt?: string;
    /** YYYY-MM periods named by the check. */
    periods: string[];
  };
  /** The line exactly as written, for anything the pills cannot say. */
  raw: string;
}

const THROUGH_RE = /Data updated through\s+(\d{4}-\d{2}-\d{2})/i;
const LOADED_RE = /\(loaded\s+(\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2})?)\)/i;
const CHECKED_RE = /checked\s+(\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2})?)/i;
const PERIOD_RE = /\b(\d{4}-\d{2})\b(?!-\d)/g;

export function parseFreshnessLine(line: string): FreshnessFacts {
  const raw = (line || '').trim().replace(/^\*\*|\*\*$/g, '');
  const facts: FreshnessFacts = { raw };
  const through = raw.match(THROUGH_RE);
  if (through) facts.through = through[1];
  const loaded = raw.match(LOADED_RE);
  if (loaded) facts.loadedAt = loaded[1];

  const at = raw.indexOf('·');
  if (at >= 0) {
    const check = raw.slice(at + 1).trim();
    const checked = check.match(CHECKED_RE);
    // Periods are named only in the "does not match" / "still loading" forms,
    // after the checked time; strip that so its date is not read as one.
    const named = check.replace(CHECKED_RE, '');
    const periods = Array.from(named.matchAll(PERIOD_RE), (m) => m[1]);
    let state: ErpState | undefined;
    if (/does not match the ERP/i.test(check)) state = 'mismatch';
    else if (/still loading/i.test(check)) state = 'loading';
    else if (/matches the ERP/i.test(check)) state = 'match';
    if (state) facts.erp = { state, checkedAt: checked?.[1], periods };
  }
  return facts;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** "2026-09-23" -> "23 Sep"; "2026-09-24 01:00" -> "24 Sep, 01:00". No
 *  Date parsing: these are ERP-local wall-clock times, not instants. */
export function humanDay(value?: string): string {
  if (!value) return '';
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}:\d{2}))?/);
  if (!m) return value;
  const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]}`;
  return m[4] ? `${day}, ${m[4]}` : day;
}

/** "2026-05" -> "May 2026". */
export function humanPeriod(value: string): string {
  const m = value.match(/^(\d{4})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}` : value;
}
